/**
 * React Query hooks for Manual Admin functionality
 *
 * Story 7-4
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { manualApi } from '../../lib/manualApi';
import type { CreateSectionInput, UpdateSectionInput, PublishVersionInput } from '../../lib/manualApi';

/**
 * Fetch all manual sections (admin view)
 */
export function useAdminSections() {
  return useQuery({
    queryKey: ['admin', 'manual', 'sections'],
    queryFn: () => manualApi.fetchAdminSections(),
  });
}

/**
 * Create new manual section
 */
export function useCreateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSectionInput) => manualApi.createSection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'manual', 'sections'] });
      queryClient.invalidateQueries({ queryKey: ['manual'] });
    },
  });
}

/**
 * Update manual section
 */
export function useUpdateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateSectionInput }) =>
      manualApi.updateSection(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'manual', 'sections'] });
      queryClient.invalidateQueries({ queryKey: ['manual'] });
    },
  });
}

/**
 * Delete manual section
 */
export function useDeleteSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => manualApi.deleteSection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'manual', 'sections'] });
      queryClient.invalidateQueries({ queryKey: ['manual'] });
    },
  });
}

/**
 * Publish new version
 */
export function usePublishVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PublishVersionInput) => manualApi.publishVersion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manual', 'changelog'] });
    },
  });
}
